import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useLocaleNavigate } from '../../../../../hooks/useLocaleNavigate';
import { Box, Button, Typography, StatusBanner, Modal } from '@lucid/lego';
import { useCartContext, useDeleteCart } from '@lucid/cart';
import { Loader } from '@lucid/lego';
import { useLabels } from '../../../../../hooks/useLabels';
import { convertToContentfulLocale } from '../../../../../utils/tools';
import {
  useSubmitCart,
  useCheckoutCart,
} from '../../../../../api/services/lupp/order/importer/importer';
import type {
  OrderPreference,
  VehicleOrderLineRequest,
} from '../../../../../api/services/lupp/order/importer/dto.importer';
import { ORDER_BUILDER_LABELS_ID } from '../../../../../config/constants';
import type { CheckoutItem, CheckoutPricing } from './types';
import Header from './Header/Header';
import SummaryPanel from './SummaryPanel/SummaryPanel';
import CartToolbar from './CartToolbar/CartToolbar';
import VehicleList from './VehicleList/VehicleList';
import styles from './Content.module.scss';

export type ContentProps = {
  theme: 'dark' | 'light';
  locale: string;
  customerId: string;
};

/**
 * Seeds per-unit order preferences from each item's context (defaulting to
 * CUSTOMER) without clobbering selections the user has already made in-session.
 * Keyed by `${itemId}:${unitIndex}` so BUILD lines of qty N address each unit.
 */
const seedOrderPrefsFromItems = (
  prev: Record<string, OrderPreference>,
  items: CheckoutItem[]
): Record<string, OrderPreference> => {
  let changed = false;
  const next = { ...prev };
  for (const item of items) {
    if (!item.itemId) continue;
    const ctx = item.itemContext ?? {};
    const orderType = ((ctx.orderType as string) ?? 'INVENTORY').toUpperCase();
    const count = orderType === 'BUILD' ? (item.quantityOrdered ?? 1) : 1;
    const prefs = Array.isArray(ctx.orderPreferences) ? (ctx.orderPreferences as string[]) : [];
    for (let i = 0; i < count; i += 1) {
      const key = `${item.itemId}:${i}`;
      if (prev[key] == null) {
        next[key] = (prefs[i] as OrderPreference) ?? 'CUSTOMER';
        changed = true;
      }
    }
  }
  return changed ? next : prev;
};

/**
 * Checkout page content — rendered inside CartProvider to access cart context.
 * @component
 * @param {ContentProps} props
 * @returns {JSX.Element}
 * @example
 * <Content theme="light" locale="en-US" customerId="abc123" />
 */
const Content: React.FC<ContentProps> = ({ theme, locale, customerId }) => {
  const navigate = useLocaleNavigate();
  const { activeCart, isLoading: isCartLoading } = useCartContext();
  const { data: labels } = useLabels({
    id: ORDER_BUILDER_LABELS_ID,
    locale: convertToContentfulLocale(locale),
  });

  const cartId = activeCart?.cartId ?? '';
  const cartItemCount = activeCart?.items?.reduce(
    (sum, item) => sum + (item.quantityOrdered ?? 1),
    0
  );
  const { mutate: deleteCart, isPending: isDeletingCart } = useDeleteCart();
  const { mutate: submitCart, isPending: isSubmittingCart } = useSubmitCart();
  const {
    mutate: checkoutCart,
    data: checkoutData,
    isPending: isCheckoutLoading,
  } = useCheckoutCart();

  // Fetch checkout details from x-importer when cartId is available, and
  // re-fetch whenever the cart item count changes (e.g. after a delete).
  useEffect(() => {
    if (cartId) {
      checkoutCart({ cartId });
    }
  }, [cartId, cartItemCount, checkoutCart]);

  const checkoutCartData = checkoutData?.cart;
  const rawItems = checkoutCartData?.items ?? activeCart?.items;
  const items = useMemo(() => (rawItems ?? []) as CheckoutItem[], [rawItems]);
  const totalVehicles =
    checkoutCartData?.itemCount ?? checkoutCartData?.lineItemCount ?? items.length;
  const pricing = checkoutCartData?.pricingSummary as CheckoutPricing | undefined;
  const isLoading = isCartLoading || isCheckoutLoading;
  const isCreditLimitExceeded =
    pricing?.remainingAfterPurchase != null && pricing.remainingAfterPurchase < 0;
  // Lucid can pause a non-compliant importer's ordering. When paused, block
  // submission and warn the importer before they attempt to order.
  const isOrdersPaused = checkoutData?.ordersPaused ?? false;

  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [redirectPath, setRedirectPath] = useState<string>('');
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [pendingVehicles, setPendingVehicles] = useState<VehicleOrderLineRequest[]>([]);

  // Per-unit order preference chosen via the Order Type dropdown, keyed by
  // `${itemId}:${unitIndex}`. This local selection is the source of truth for
  // the submit payload — the choice is submit-time only and needs no cart round-trip.
  const [orderPrefs, setOrderPrefs] = useState<Record<string, OrderPreference>>({});

  useEffect(() => {
    setOrderPrefs((prev) => seedOrderPrefsFromItems(prev, items));
  }, [items]);

  const handleOrderPreferenceChange = useCallback(
    (itemId: string, unitIndex: number, value: OrderPreference) => {
      setOrderPrefs((prev) => ({ ...prev, [`${itemId}:${unitIndex}`]: value }));
    },
    []
  );

  useEffect(() => {
    if (!successMessage) return;
    const id = setTimeout(() => {
      navigate(redirectPath || '/order-hub/order-desk');
    }, 3000);
    return () => clearTimeout(id);
  }, [successMessage, redirectPath, navigate]);

  const toggleItem = useCallback((id: string) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const buildVehiclesArray = useCallback(() => {
    // Expand each cart item into one or more vehicle line requests, grouping
    // the item's individual units by their per-vehicle order preference (chosen
    // via the Order Type dropdown and held in `orderPrefs`) so that a single
    // BUILD line of qty N can carry a mix of order types. Preferences are a
    // submit-time choice kept in local state — no cart round-trip required.
    const vehicles = items.flatMap<VehicleOrderLineRequest>((item) => {
      const ctx = item.itemContext ?? {};
      const orderType = (ctx.orderType as 'BUILD' | 'INVENTORY') ?? 'INVENTORY';
      const count = orderType === 'BUILD' ? (item.quantityOrdered ?? 1) : 1;
      const groups = new Map<OrderPreference, number>();
      for (let i = 0; i < count; i += 1) {
        const pref = (item.itemId ? orderPrefs[`${item.itemId}:${i}`] : undefined) ?? 'CUSTOMER';
        groups.set(pref, (groups.get(pref) ?? 0) + 1);
      }
      const base = {
        vin: ctx.vin as string | undefined,
        shortcode: ctx.shortcode as string | undefined,
        model: ctx.model as string | undefined,
        batchNumber: ctx.batchNumber as string | undefined,
        vehicleId: ctx.uuid as string | undefined,
      };
      return Array.from(groups.entries()).map(([orderPreference, qty]) => ({
        type: orderType,
        ...(orderType === 'BUILD' ? { qty } : {}),
        orderPreference,
        ...base,
      }));
    });
    return vehicles;
  }, [items, orderPrefs]);

  const handleSubmitCart = useCallback(() => {
    if (!cartId || items.length === 0) return;
    // Build vehicles array and show confirmation modal
    const vehicles = buildVehiclesArray();
    setPendingVehicles(vehicles);
    setIsConfirmationOpen(true);
  }, [cartId, items.length, buildVehiclesArray]);

  const handleConfirmSubmitCart = useCallback(() => {
    if (!cartId || pendingVehicles.length === 0) return;
    submitCart(
      { cartId, vehicles: pendingVehicles },
      {
        onSuccess: (result) => {
          const orderNumber = result?.order?.orderNumber;
          setErrorMessage(null);
          setRedirectPath(
            orderNumber ? `/order-hub/order-desk/${orderNumber}` : '/order-hub/order-desk'
          );
          setSuccessMessage('Order submitted successfully! Redirecting...');
          setIsConfirmationOpen(false);
        },
        onError: () => {
          // Never surface raw backend errors (e.g. internal cart IDs) to partners.
          setErrorMessage(
            labels?.['content.submitError'] ??
              'We were unable to submit your order. Please try again, or contact support if the problem continues.'
          );
          setIsConfirmationOpen(false);
        },
      }
    );
  }, [cartId, pendingVehicles, submitCart, labels]);

  const handleCancelConfirmation = useCallback(() => {
    setIsConfirmationOpen(false);
    setPendingVehicles([]);
  }, []);

  return (
    <Box theme={theme} data-testid="order-builder-checkout-page">
      {isSubmittingCart && (
        <Box className={styles.submittingOverlay} data-testid="submitting-overlay">
          <Loader theme="light" />
        </Box>
      )}
      {successMessage && (
        <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.successMessage}>
          <Typography variant="body" size="medium" color="white" theme={theme}>
            {successMessage}
          </Typography>
        </Box>
      )}
      {errorMessage && (
        <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.errorMessage}>
          <Typography variant="body" size="medium" color="white" theme={theme}>
            {errorMessage}
          </Typography>
        </Box>
      )}
      {!isLoading && items.length > 0 && (
        <Header
          theme={theme}
          labels={labels}
          onSubmit={handleSubmitCart}
          isSubmitting={isSubmittingCart}
          isDisabled={isCreditLimitExceeded || isOrdersPaused}
        />
      )}
      <Button
        type="tertiary"
        color="neutral"
        theme={theme}
        dataTestId="checkout-back-button"
        gaComponent="order-builder"
        label={labels?.back ?? 'Back'}
        iconLeft={{ category: 'arrows', name: 'arrow-left' }}
        handleClick={() => navigate(-1)}
      />
      {!isLoading && items.length === 0 && (
        <Box className={styles.emptyCart} data-testid="checkout-empty-cart">
          <Typography variant="headline" size="small" color="white" theme={theme}>
            {labels?.['content.emptyCart'] ?? 'Your cart is empty.'}
          </Typography>
          <Typography variant="body" size="medium" color="gray-3" theme={theme}>
            {labels?.['content.emptyCartSubtitle'] ?? 'Add vehicles to get started.'}
          </Typography>
          <Box className={styles.emptyCartActions}>
            <Button
              type="secondary"
              color="neutral"
              theme={theme}
              dataTestId="empty-cart-configure-btn"
              gaComponent="order-builder-checkout"
              label={labels?.['toolbar.configureNewVehicle'] ?? 'Configure New Vehicle'}
              iconLeft={{ category: 'global', name: 'plus' }}
              handleClick={() => navigate('/order-hub/order-builder#configurator')}
            />
            <Button
              type="primary"
              color="neutral"
              theme={theme}
              dataTestId="empty-cart-add-available-btn"
              gaComponent="order-builder-checkout"
              label={labels?.['toolbar.addAvailableVehicle'] ?? 'Add Available Vehicle'}
              iconLeft={{ category: 'global', name: 'plus' }}
              handleClick={() => navigate('/order-hub/order-builder#available-vehicles')}
            />
          </Box>
        </Box>
      )}
      {!isLoading && items.length > 0 && (
        <>
          <StatusBanner
            type="info"
            message={
              labels?.['content.addingAVehicle'] ??
              'Adding a vehicle to your cart does not guarantee availability. Order matching takes place after you submit.'
            }
            dataTestId="checkout-availability-info-banner"
            className={styles.availabilityInfoBanner}
          />
          {isCreditLimitExceeded && (
            <StatusBanner
              type="error"
              theme="light"
              message={
                labels?.['content.orderBlockedCredit'] ?? 'Order Blocked — Credit Limit Exceeded'
              }
              subtitle={
                labels?.['content.creditLimitSubtitle'] ??
                'Please pay unpaid invoices or adjust your order to be within available credit to continue.'
              }
              dataTestId="credit-limit-exceeded-banner"
              className={styles.creditLimitBanner}
            />
          )}
          {isOrdersPaused && (
            <StatusBanner
              type="error"
              theme="light"
              message={labels?.['content.orderingPaused'] ?? 'Ordering Paused'}
              subtitle={
                labels?.['content.orderingPausedSubtitle'] ??
                'Lucid has temporarily paused new orders for your account. Please pay any unpaid invoices and contact your Lucid representative for assistance.'
              }
              dataTestId="orders-paused-banner"
              className={styles.creditLimitBanner}
            />
          )}
          <SummaryPanel
            labels={labels}
            pricing={pricing}
            locale={locale}
            totalVehicles={totalVehicles}
          />
          <CartToolbar
            theme={theme}
            invertedTheme={invertedTheme}
            labels={labels}
            totalVehicles={totalVehicles}
            pricing={pricing}
            locale={locale}
            onDeleteCart={() =>
              deleteCart(
                { cartId, customerId },
                { onSuccess: () => navigate('/order-hub/order-builder') }
              )
            }
            isDeletingCart={isDeletingCart}
            onConfigureNewVehicle={() => navigate('/order-hub/order-builder#configurator')}
            onAddAvailableVehicle={() => navigate('/order-hub/order-builder#available-vehicles')}
          />
          <VehicleList
            theme={theme}
            labels={labels}
            isLoading={isLoading}
            items={items}
            locale={locale}
            expandedItems={expandedItems}
            onToggle={toggleItem}
            customerId={customerId}
            orderPrefs={orderPrefs}
            onOrderPreferenceChange={handleOrderPreferenceChange}
            onAllItemsDeleted={() =>
              deleteCart(
                { cartId, customerId },
                { onSuccess: () => navigate('/order-hub/order-builder') }
              )
            }
          />
        </>
      )}
      <Modal
        isOpen={isConfirmationOpen}
        onClose={handleCancelConfirmation}
        title={labels?.['modal.orderConfirmation.title'] ?? 'Order Confirmation'}
        theme={invertedTheme}
        size="medium"
        dataTestId="order-confirmation-modal"
        footer={
          <Box className={styles.modalFooter}>
            <Button
              label={labels?.['modal.orderConfirmation.cancel'] ?? 'CANCEL'}
              type="secondary"
              color="neutral"
              theme={invertedTheme}
              handleClick={handleCancelConfirmation}
              gaComponent="order-confirmation-cancel"
              dataTestId="order-confirmation-cancel-btn"
              isFluid={true}
            />
            <Button
              label={labels?.['modal.orderConfirmation.submit'] ?? 'SUBMIT ORDER'}
              type="primary"
              color="neutral"
              theme={invertedTheme}
              handleClick={handleConfirmSubmitCart}
              gaComponent="order-confirmation-submit"
              dataTestId="order-confirmation-submit-btn"
              isFluid={true}
            />
          </Box>
        }
      >
        <Box className={styles.modalContent}>
          <Typography variant="body" size="medium" theme={invertedTheme} color="white">
            {labels?.['modal.orderConfirmation.message'] ??
              'Verify the Order Type is updated for each order line in the Cart.'}
          </Typography>
          <Typography variant="body" size="medium" theme={invertedTheme} color="white">
            {labels?.['modal.orderConfirmation.info'] ??
              'Order Confirmation will be available for download post submission.'}
          </Typography>
          <Typography variant="body" size="medium" theme={invertedTheme} color="white">
            {labels?.['modal.orderConfirmation.confirm'] ?? 'Submit this order?'}
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
};

export default Content;
